// src/planner/EventGraph.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import EventNode from './EventNode';
import { color, font } from '../../theme/tokens';

const GraphViewport = styled.div`
    width: 100%; height: 100%;
    position: relative; overflow: hidden;
    cursor: grab;
    &:active { cursor: grabbing; }
    touch-action: none;
`;

const GraphContent = styled.div.attrs(props => ({
    style: { transform: `translate(${props.$pan.x}px, ${props.$pan.y}px) scale(${props.$zoom})` }
}))`
    position: absolute; transform-origin: 0 0; will-change: transform;
`;

const TodayLine = styled.div`
    position: absolute; left: 0; top: -5000px; bottom: -5000px;
    width: 1px; background-color: ${color.blood};
    z-index: 1;

    &::after {
        content: '今日';
        position: absolute; top: 5040px; left: 6px;
        padding: 2px 8px;
        background-color: ${color.blood}; color: ${color.onBlood};
        font-family: ${font.display}; font-size: 12px; letter-spacing: 0.2em;
        white-space: nowrap;
    }
`;

const ConnectionPath = styled.div`
    position: absolute;
    background-color: ${props => props.$color || color.text3};
    z-index: 5; pointer-events: none;
    opacity: 0.75;
`;
const HorizontalLine = styled(ConnectionPath)` height: 1px; `;
const VerticalLine = styled(ConnectionPath)` width: 1px; `;

const CentralTimeline = styled.div`
    position: absolute; height: 1px;
    background: linear-gradient(90deg, transparent, ${color.line2} 15%, ${color.line2} 85%, transparent);
    z-index: 2;
`;

const CARD_WIDTH = 300;
const CARD_HEIGHT = 80;

function EventGraph({ nodes, edges, allEvents, expandedNodes, onNodeExpand, ...nodeHandlers }) {
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const viewportRef = useRef(null);

    const timelineRange = useMemo(() => {
        if (!nodes.length) return { minX: -5000, maxX: 5000 };
        const xPositions = nodes.map(node => node.position.x);
        return { minX: Math.min(...xPositions) - 2000, maxX: Math.max(...xPositions) + 2000 };
    }, [nodes]);

    useEffect(() => {
        if (viewportRef.current) {
            const { width, height } = viewportRef.current.getBoundingClientRect();
            setPan({ x: width / 2, y: height / 3 });
        }
    }, []);

    const panState = useRef({ isPanning: false, startPos: { x: 0, y: 0 }, startPan: { x: 0, y: 0 } });
    
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        const getEventCoordinates = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

        const handlePanStart = (e) => {
            const el = e.target;
            if (!(el.tagName === 'BUTTON' || el.tagName === 'INPUT' || el.closest('button') || el.closest('input'))) {
                e.preventDefault();
                panState.current = { isPanning: true, startPos: getEventCoordinates(e), startPan: pan };
                viewport.style.cursor = 'grabbing';
            }
        };
        const handlePanMove = (e) => {
            if (!panState.current.isPanning) return;
            const coords = getEventCoordinates(e);
            setPan({
                x: panState.current.startPan.x + (coords.x - panState.current.startPos.x),
                y: panState.current.startPan.y + (coords.y - panState.current.startPos.y)
            });
        };
        const handlePanEnd = () => {
            panState.current.isPanning = false;
            viewport.style.cursor = 'grab';
        };

        viewport.addEventListener('mousedown', handlePanStart);
        document.addEventListener('mousemove', handlePanMove);
        document.addEventListener('mouseup', handlePanEnd);
        viewport.addEventListener('touchstart', handlePanStart, { passive: false });
        document.addEventListener('touchmove', handlePanMove);
        document.addEventListener('touchend', handlePanEnd);
        return () => {
            viewport.removeEventListener('mousedown', handlePanStart);
            document.removeEventListener('mousemove', handlePanMove);
            document.removeEventListener('mouseup', handlePanEnd);
            viewport.removeEventListener('touchstart', handlePanStart);
            document.removeEventListener('touchmove', handlePanMove);
            document.removeEventListener('touchend', handlePanEnd);
        };
    }, [pan]);

    // native non-passive listener: React's onWheel is passive, so preventDefault
    // there cannot stop the page (which now scrolls) from scrolling while zooming
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            setZoom((z) => Math.max(0.1, Math.min(2, e.deltaY > 0 ? z - 0.05 : z + 0.05)));
        };
        viewport.addEventListener('wheel', onWheel, { passive: false });
        return () => viewport.removeEventListener('wheel', onWheel);
    }, []);

    const nodeMap = useMemo(() => new Map(nodes.map(node => [node.id, node])), [nodes]);
    const CENTRAL_LINE_Y = -CARD_HEIGHT;

    const renderConnections = () => {
        const connections = [];
        edges.forEach(edge => {
            const src = nodeMap.get(edge.source);
            const tgt = nodeMap.get(edge.target);
            if (!src || !tgt) return;

            const sx = src.position.x + CARD_WIDTH, sy = src.position.y + CARD_HEIGHT / 2;
            const tx = tgt.position.x, ty = tgt.position.y + CARD_HEIGHT / 2;
            const ext = 30, interY = sy < ty ? ty - CARD_HEIGHT/2 : ty + CARD_HEIGHT/2;

            connections.push(
                <HorizontalLine key={`h1-${edge.id}`} $color={edge.color} style={{left: sx, top: sy-1, width: ext}} />,
                <VerticalLine key={`v1-${edge.id}`} $color={edge.color} style={{left: sx+ext-1, top: Math.min(sy, interY), height: Math.abs(interY-sy)}} />,
                <HorizontalLine key={`h2-${edge.id}`} $color={edge.color} style={{left: Math.min(sx+ext, tx-ext), top: interY-1, width: Math.abs(tx-ext - (sx+ext))}} />
            );
            if (interY !== ty) connections.push(<VerticalLine key={`v2-${edge.id}`} $color={edge.color} style={{left: tx-ext-1, top: Math.min(interY, ty), height: Math.abs(ty-interY)}} />);
            connections.push(<HorizontalLine key={`h3-${edge.id}`} $color={edge.color} style={{left: tx-ext, top: ty-1, width: ext}} />);
        });

        nodes.forEach(node => {
            if (!node.data.parent_id) {
                const nx = node.position.x, ny = node.position.y + CARD_HEIGHT/2, ext = 30;
                connections.push(<HorizontalLine key={`rh-${node.id}`} $color={node.data.color} style={{left: nx-ext, top: ny-1, width: ext}} />);
                if (ny !== CENTRAL_LINE_Y) connections.push(<VerticalLine key={`rv-${node.id}`} $color={node.data.color} style={{left: nx-ext-1, top: Math.min(ny, CENTRAL_LINE_Y), height: Math.abs(CENTRAL_LINE_Y-ny)}} />);
                connections.push(<HorizontalLine key={`rt-${node.id}`} $color={node.data.color} style={{left: 0, top: CENTRAL_LINE_Y-1, width: nx-ext}} />);
            }
        });
        return connections;
    };

    return (
        <GraphViewport ref={viewportRef}>
            <GraphContent $pan={pan} $zoom={zoom}>
                <TodayLine />
                <CentralTimeline style={{ top: CENTRAL_LINE_Y, left: timelineRange.minX, width: timelineRange.maxX - timelineRange.minX }} />
                {renderConnections()}
                {nodes.map(node => (
                    <EventNode key={node.id} event={node.data} position={node.position} allEvents={allEvents} expandedNodes={expandedNodes} onNodeExpand={onNodeExpand} {...nodeHandlers} />
                ))}
            </GraphContent>
        </GraphViewport>
    );
}

export default EventGraph;